import { useEffect, useRef, useState } from "react";

export const STEPS = [
  { n: 1, label: "Upload", testid: "nav-step-1" },
  { n: 2, label: "AI Render", testid: "nav-step-2" },
  { n: 3, label: "3D Print", testid: "nav-step-3" },
];

const FADE = 28;
const mask = ({ left, right }) => {
  if (!left && !right) return undefined;
  const stops = [left ? "transparent, #000 " + FADE + "px" : "#000", right ? `#000 calc(100% - ${FADE}px), transparent` : "#000"];
  return `linear-gradient(to right, ${stops.join(", ")})`;
};

// Which edges still hide content — drives the faded-edge "it scrolls" hint.
const useEdges = (ref) => {
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () =>
      setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", update); ro?.disconnect(); };
  }, [ref]);
  return edges;
};

export const StepRail = ({ step, onStepClick, className = "", id }) => {
  const ref = useRef(null);
  const edges = useEdges(ref);

  // Keep the current step in view whenever it changes (horizontal only — never scrolls the page).
  useEffect(() => {
    const el = ref.current;
    const pill = el?.querySelector('[data-active="true"]');
    if (!el || !pill) return;
    const left = pill.offsetLeft - (el.clientWidth - pill.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [step]);

  const onKeyDown = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const pills = [...e.currentTarget.querySelectorAll("button")];
    const i = pills.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    pills[(i + (e.key === "ArrowRight" ? 1 : -1) + pills.length) % pills.length]?.focus();
  };

  const m = mask(edges);
  return (
    <nav
      ref={ref}
      id={id}
      aria-label="Studio steps"
      data-testid={id || "step-rail"}
      data-scroll-left={edges.left ? "true" : "false"}
      data-scroll-right={edges.right ? "true" : "false"}
      onKeyDown={onKeyDown}
      style={m ? { maskImage: m, WebkitMaskImage: m } : undefined}
      className={`flex items-center gap-1 rounded-full bg-muted/60 p-1 min-w-0 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-px-1 overscroll-x-contain ${className}`}
    >
      {STEPS.map((s) => {
        const active = step === s.n;
        const done = step > s.n;
        return (
          <button
            key={s.n}
            type="button"
            data-testid={id ? `${id}-${s.n}` : s.testid}
            data-active={active ? "true" : "false"}
            aria-current={active ? "step" : undefined}
            onClick={() => (s.n <= step ? onStepClick(s.n) : null)}
            className={`flex items-center gap-2 shrink-0 snap-start whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              active ? "bg-primary text-primary-foreground shadow" : done ? "text-foreground hover:bg-background" : "text-muted-foreground cursor-not-allowed"
            }`}
          >
            <span className={`grid place-items-center h-5 w-5 rounded-full text-[11px] font-bold ${active || done ? "bg-accent text-accent-foreground" : "bg-border text-muted-foreground"}`}>
              {s.n}
            </span>
            {s.label}
          </button>
        );
      })}
    </nav>
  );
};
